import React, { useRef, useMemo, useState, useEffect, Suspense } from 'react';
import PropTypes from 'prop-types';
import { PieChart } from 'echarts/charts';
import {
    GridComponent,
    LegendComponent,
    TitleComponent,
    TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../../../providers/AppProvider';
import { Spinner } from 'react-bootstrap';

// Carga diferida del componente ReactEchart
const ReactEchart = React.lazy(() =>
    new Promise(resolve => {
        setTimeout(() => {
            resolve(import('../../../../../components/common/ReactEchart'));
        }, 100);
    })
);

// Registrar componentes de ECharts
echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    PieChart,
    CanvasRenderer,
    LegendComponent
]);

// Colores predefinidos para los vehículos
const VEHICLE_COLORS = [
    '#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de',
    '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc', '#ff9f7f',
    '#87ceeb', '#dda0dd', '#98fb98', '#f0e68c', '#cd853f'
];

const getOptions = (data, getThemeColor, totalKM) => {
    if (!getThemeColor || typeof getThemeColor !== 'function') {
        console.warn("getThemeColor is not a function or is undefined");
        return {};
    }

    return {
        tooltip: {
            trigger: 'item',
            confine: true,
            padding: [7, 10],
            backgroundColor: '#fff',
            borderColor: '#ccc',
            textStyle: { color: '#000' },
            borderWidth: 1,
            transitionDuration: 0,
            formatter: function (params) {
                const kmValue = params.data.totalKM || 0;
                return `<strong>${params.data.name}:</strong><br/>${kmValue.toFixed(2)} km (${params.percent}%)`;
            }
        },
        legend: { show: false },
        animation: false,
        series: [
            {
                name: 'Vehículos KM',
                type: 'pie',
                radius: ['100%', '67%'],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderWidth: 2,
                    borderColor: '#fff'
                },
                label: {
                    show: false,
                    formatter: '{b}: {d}%'
                },
                emphasis: {
                    scale: false
                },
                labelLine: { show: false },
                data: data.map((vehiculo, index) => ({
                    value: vehiculo.totalKM,
                    name: vehiculo.title,
                    totalKM: vehiculo.totalKM,
                    vehiculoUid: vehiculo.vehiculoUid,
                    itemStyle: {
                        color: vehiculo.color_vehiculo || VEHICLE_COLORS[index % VEHICLE_COLORS.length]
                    }
                }))
            }
        ]
    };
};

const VehiculoKilometrajeChart = ({ data, onSelectVehicle, totalKM }) => {
    const chartRef = useRef(null);
    const { getThemeColor } = useAppContext() || {};
    const [loadingState, setLoadingState] = useState('initial');

    // Memoizar los datos del gráfico
    const chartData = useMemo(() => {
        if (!data || data.length === 0) return [];

        // Limitar a los primeros 10 vehículos para mejor visualización
        /*const MAX_VEHICLES = 10;
        
        if (data.length <= MAX_VEHICLES) {*/
        return data;
        /* }
 
         // Tomar los primeros 9 y agrupar el resto como "Otros"
         const topVehicles = data.slice(0, MAX_VEHICLES - 1);
         const otherVehicles = data.slice(MAX_VEHICLES - 1);
         const otherTotalKM = otherVehicles.reduce((sum, v) => sum + v.totalKM, 0);
 
         return [
             ...topVehicles,
             {
                 title: 'Otros',
                 totalKM: otherTotalKM,
                 vehiculoUid: 'otros',
                 color_vehiculo: '#CCCCCC'
             }
         ];*/
    }, [data]);

    const chartOptions = useMemo(() =>
        getOptions(chartData, getThemeColor, totalKM),
        [chartData, getThemeColor, totalKM]
    );

    useEffect(() => {
        if (data && data.length > 0) {
            setLoadingState('completed');
        }
    }, [data]);

    const events = useMemo(() => ({
        click: (params) => {
            if (params.data && params.data.vehiculoUid && params.data.vehiculoUid !== 'otros') {
                onSelectVehicle({
                    title: params.data.name,
                    id: params.data.vehiculoUid,
                    totalKM: params.data.totalKM
                });
            }
        }
    }), [onSelectVehicle]);

    if (loadingState === 'initial') {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '12.5rem' }}>
                <Spinner animation="border" variant="primary" size="sm" />
                <span className="ms-2">Cargando gráfico...</span>
            </div>
        );
    }

    return (
        <div className="position-relative py-2">
            <Suspense fallback={
                <div className="d-flex justify-content-center align-items-center" style={{ height: '12.5rem' }}>
                    <Spinner animation="border" variant="primary" size="sm" />
                    <span className="ms-2">Cargando gráfico...</span>
                </div>
            }>
                <ReactEchart
                    ref={chartRef}
                    echarts={echarts}
                    option={chartOptions}
                    onEvents={events}
                    style={{ height: '18.5rem' }}
                    notMerge={true}
                    lazyUpdate={true}
                    opts={{
                        renderer: 'canvas',
                        devicePixelRatio: window.devicePixelRatio || 1
                    }}
                />
            </Suspense>
            <div className="position-absolute top-50 start-50 translate-middle text-center">
                <p className="fs-10 mb-0 text-400 font-sans-serif fw-medium">Total KM</p>
                <p className="fs-6 mb-0 font-sans-serif fw-medium mt-n2">{totalKM.toFixed(0)} km</p>
            </div>
        </div>
    );
};

VehiculoKilometrajeChart.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            totalKM: PropTypes.number.isRequired,
            vehiculoUid: PropTypes.string.isRequired,
            color_vehiculo: PropTypes.string
        })
    ).isRequired,
    onSelectVehicle: PropTypes.func.isRequired,
    totalKM: PropTypes.number.isRequired
};

export default React.memo(VehiculoKilometrajeChart);